"use client";

import { useEffect, useState, useCallback, useRef, Suspense } from "react";
import { useSession } from "next-auth/react";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { Check, Zap, ArrowLeft, Loader2 } from "lucide-react";
import { Toast } from "@/components/Toast";
import {
  fetchBillingState,
  createCheckoutSession,
  createBillingPortalSession,
  BillingState,
} from "@/lib/api";

type ToastState = { kind: "success" | "error" | "info"; message: string } | null;

const FREE_FEATURES = [
  "Up to 100 job scores per month",
  "AI-powered job fit scoring",
  "Resume analysis",
  "Email job forwarding",
  "Job board integrations",
];

const PRO_FEATURES = [
  "Unlimited job scores",
  "Priority AI scoring",
  "Advanced analytics & trends",
  "All Free features",
];

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30 seconds

function PricingPageInner() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const pollAttempts = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string, kind: "success" | "error" | "info" = "error") => {
    setToast({ message, kind });
  };

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const pollBillingState = useCallback(() => {
    pollAttempts.current += 1;
    fetchBillingState()
      .then((state) => {
        setBilling(state);
        if (state.is_pro) {
          stopPolling();
          setActivating(false);
          router.replace("/app/settings/billing?upgraded=1");
        } else if (pollAttempts.current >= POLL_MAX_ATTEMPTS) {
          stopPolling();
          setActivating(false);
          showToast(
            "Activation is taking longer than expected — refresh in a moment.",
            "info"
          );
        } else {
          pollTimer.current = setTimeout(pollBillingState, POLL_INTERVAL_MS);
        }
      })
      .catch(() => {
        if (pollAttempts.current >= POLL_MAX_ATTEMPTS) {
          stopPolling();
          setActivating(false);
        } else {
          pollTimer.current = setTimeout(pollBillingState, POLL_INTERVAL_MS);
        }
      });
  }, [router, stopPolling]);

  useEffect(() => {
    if (!session) return;
    const isSuccess = searchParams.get("success") === "1";
    if (isSuccess) {
      setActivating(true);
      pollAttempts.current = 0;
      pollBillingState();
    } else {
      setLoadingBilling(true);
      fetchBillingState()
        .then(setBilling)
        .catch(() => {})
        .finally(() => setLoadingBilling(false));
    }
    return stopPolling;
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpgrade = useCallback(async () => {
    setCheckoutLoading(true);
    try {
      const { url } = await createCheckoutSession("pro");
      window.location.href = url;
    } catch (err: any) {
      showToast(err?.message ?? "Unable to start checkout — please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  }, []);

  const handleManageBilling = useCallback(async () => {
    setPortalLoading(true);
    try {
      const { url } = await createBillingPortalSession();
      window.location.href = url;
    } catch (err: any) {
      showToast(err?.message ?? "Unable to open billing portal — please try again.");
    } finally {
      setPortalLoading(false);
    }
  }, []);

  const isPro = billing?.is_pro ?? false;
  const isCurrentFree = !isPro;
  const billingEnabled = billing?.billing_enabled ?? true;

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          kind={toast.kind}
          onClose={() => setToast(null)}
          durationMs={4000}
        />
      )}

      {activating && (
        <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-center gap-3 bg-indigo-600 px-4 py-3 text-sm font-medium text-white">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          Activating your Pro subscription…
        </div>
      )}

      <div className="mx-auto max-w-4xl px-4 py-16">
        {/* Back link */}
        {session && (
          <div className="mb-8">
            <Link
              href="/app"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to dashboard
            </Link>
          </div>
        )}

        <div className="text-center mb-12">
          <h1 className="text-2xl font-bold text-gray-900">Simple, transparent pricing</h1>
          <p className="mt-3 text-gray-500">
            Start free. Upgrade when you&apos;re ready.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Free card */}
          <div className={`relative rounded-2xl border bg-white p-8 shadow-sm ${isCurrentFree ? "border-gray-900 ring-2 ring-gray-900" : "border-gray-200"}`}>
            {isCurrentFree && billing && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-gray-900 px-3 py-0.5 text-xs font-medium text-white">
                  Current plan
                </span>
              </div>
            )}
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900">Free</h2>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-400">/mo</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">Forever free. No credit card required.</p>
            </div>
            <ul className="mb-8 space-y-3">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="rounded-lg border border-gray-200 bg-gray-50 py-2.5 text-center text-sm font-medium text-gray-500">
              {isCurrentFree ? "Your current plan" : "Free plan"}
            </div>
          </div>

          {/* Pro card */}
          <div className={`relative rounded-2xl border bg-white p-8 shadow-sm ${isPro ? "border-indigo-600 ring-2 ring-indigo-600" : "border-gray-200"}`}>
            {isPro && billing && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-indigo-600 px-3 py-0.5 text-xs font-medium text-white">
                  Current plan
                </span>
              </div>
            )}
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">Pro</h2>
                <Zap className="h-4 w-4 text-indigo-500" />
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-gray-900">$8</span>
                <span className="text-gray-400">/mo</span>
              </div>
              <p className="mt-2 text-sm text-gray-500">For serious job seekers.</p>
            </div>
            <ul className="mb-8 space-y-3">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                  {f}
                </li>
              ))}
            </ul>

            {/* Pro CTA */}
            {!session ? (
              <Link
                href="/login"
                className="flex w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
              >
                Get started
              </Link>
            ) : isPro ? (
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-60"
              >
                {portalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Manage billing
              </button>
            ) : billingEnabled ? (
              /* Coming soon when stripe_price_id is null — the API returns 400,
                 but we optimistically disable based on a placeholder check.
                 The button remains enabled; if checkout is unavailable the API
                 returns a clear 400 which surfaces as a toast. */
              <button
                onClick={handleUpgrade}
                disabled={checkoutLoading || loadingBilling}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                {checkoutLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Upgrade to Pro
              </button>
            ) : null}
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-gray-400">
          Prices in USD. Cancel anytime. Payments secured by Stripe.
        </p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  return (
    <Suspense>
      <PricingPageInner />
    </Suspense>
  );
}
